import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// ═══════════════════════════════════════════════════════════════════════
// TYPES & DATA
// ═══════════════════════════════════════════════════════════════════════

interface MorphItem {
  id: string;
  chaosColor: 'yellow' | 'pink' | 'blue' | 'green' | 'orange';
  chaosText: string;
  chaosX: number;
  chaosY: number;
  chaosRot: number;
  targetX: number;
  targetY: number;
  nodeType: 'goal' | 'task' | 'event' | 'contact' | 'note';
  nodeTypeName: string;
  nodeTitle: string;
  nodeBadge: string;
}

interface DecoItem {
  type: 'paper' | 'text';
  html?: string;
  text?: string;
  x: number;
  y: number;
  rot: number;
}

interface CanvasEdgeDef {
  id: string;
  from: string;
  to: string;
  color: string;
}

const MORPH_ITEMS: MorphItem[] = [
  {
    id: 'obj',
    chaosColor: 'yellow',
    chaosText: 'Japon 3 semaines !!!',
    chaosX: 45,
    chaosY: 38,
    chaosRot: -4,
    targetX: 50,
    targetY: 82,
    nodeType: 'goal',
    nodeTypeName: 'Objectif',
    nodeTitle: 'Voyage au Japon',
    nodeBadge: '3 semaines',
  },
  {
    id: 't1',
    chaosColor: 'blue',
    chaosText: 'vols tokyo a reserver',
    chaosX: 8,
    chaosY: 62,
    chaosRot: 6,
    targetX: 20,
    targetY: 45,
    nodeType: 'task',
    nodeTypeName: 'Tache',
    nodeTitle: 'Reserver les vols',
    nodeBadge: 'Haute',
  },
  {
    id: 't2',
    chaosColor: 'green',
    chaosText: 'itineraire + hotels',
    chaosX: 68,
    chaosY: 65,
    chaosRot: -7,
    targetX: 50,
    targetY: 45,
    nodeType: 'task',
    nodeTypeName: 'Tache',
    nodeTitle: 'Planifier itineraire',
    nodeBadge: 'En cours',
  },
  {
    id: 't3',
    chaosColor: 'orange',
    chaosText: 'checklist valise',
    chaosX: 78,
    chaosY: 28,
    chaosRot: 5,
    targetX: 80,
    targetY: 45,
    nodeType: 'task',
    nodeTypeName: 'Tache',
    nodeTitle: 'Preparer les valises',
    nodeBadge: 'A faire',
  },
  {
    id: 'co',
    chaosColor: 'pink',
    chaosText: 'Keiko - amie Tokyo',
    chaosX: 12,
    chaosY: 18,
    chaosRot: -6,
    targetX: 20,
    targetY: 12,
    nodeType: 'contact',
    nodeTypeName: 'Contact',
    nodeTitle: 'Keiko Yamamoto',
    nodeBadge: 'Tokyo',
  },
  {
    id: 'ev',
    chaosColor: 'blue',
    chaosText: 'DEPART 15 MAI',
    chaosX: 38,
    chaosY: 12,
    chaosRot: 3,
    targetX: 50,
    targetY: 12,
    nodeType: 'event',
    nodeTypeName: 'Evenement',
    nodeTitle: 'Vol depart',
    nodeBadge: '15 mai',
  },
  {
    id: 'no',
    chaosColor: 'yellow',
    chaosText: 'restos a tester !',
    chaosX: 58,
    chaosY: 18,
    chaosRot: -5,
    targetX: 80,
    targetY: 12,
    nodeType: 'note',
    nodeTypeName: 'Note',
    nodeTitle: 'Restaurants',
    nodeBadge: 'Tokyo',
  },
];

const DECO_ITEMS: DecoItem[] = [
  {
    type: 'paper',
    html: '<span class="strike">reserver train</span><br><span class="strike">changer euros</span><br>acheter guide<br><span class="strike">visa ?</span>',
    x: 28,
    y: 77,
    rot: 3,
  },
  { type: 'text', text: '"ne pas oublier le passeport..."', x: 24, y: 42, rot: -2 },
  { type: 'text', text: 'budget hotels = ?', x: 60, y: 42, rot: 4 },
  {
    type: 'paper',
    html: '• adaptateur prise<br>• <span class="strike">app traduction</span><br>• carte SIM',
    x: 88,
    y: 82,
    rot: -4,
  },
];

const EDGES: CanvasEdgeDef[] = [
  { id: 'co-t1', from: 'co', to: 't1', color: 'rgba(52, 211, 153, 0.45)' },
  { id: 'ev-t2', from: 'ev', to: 't2', color: 'rgba(245, 158, 11, 0.45)' },
  { id: 'no-t3', from: 'no', to: 't3', color: 'rgba(168, 162, 158, 0.4)' },
  { id: 't1-obj', from: 't1', to: 'obj', color: 'rgba(139, 92, 246, 0.5)' },
  { id: 't2-obj', from: 't2', to: 'obj', color: 'rgba(139, 92, 246, 0.5)' },
  { id: 't3-obj', from: 't3', to: 'obj', color: 'rgba(139, 92, 246, 0.5)' },
];

// ═══════════════════════════════════════════════════════════════════════
// CSS
// ═══════════════════════════════════════════════════════════════════════

const SOCLE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Kalam:wght@400;700&display=swap');

#socle-stage {
  position: relative;
  width: 100%;
  height: 520px;
  background: #0a0a0f;
  border: 1px solid rgba(39, 39, 42, 0.8);
  border-radius: 16px;
  overflow: hidden;
}
#socle-stage::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(139, 92, 246, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(139, 92, 246, 0.04) 1px, transparent 1px);
  background-size: 40px 40px;
  opacity: 0;
  transition: opacity 1s ease;
  pointer-events: none;
}
#socle-stage.phase-canvas::before,
#socle-stage.phase-completion::before { opacity: 1; }

.morph-item {
  position: absolute;
  width: 170px;
  height: 82px;
  transition: left 1.4s cubic-bezier(0.65, 0, 0.35, 1),
              top 1.4s cubic-bezier(0.65, 0, 0.35, 1),
              transform 1.4s cubic-bezier(0.65, 0, 0.35, 1),
              background 1.4s ease,
              box-shadow 1.4s ease,
              border-color 1.4s ease,
              border-width 1.4s ease,
              border-radius 1.4s ease;
  z-index: 5;
  background: transparent;
  border: 0 solid transparent;
  border-radius: 2px;
}
.morph-item.chaos-state {
  box-shadow: 0 6px 20px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.3);
  border-radius: 2px;
}
.morph-item.chaos-postit-yellow { background: #fde68a; }
.morph-item.chaos-postit-pink { background: #fbcfe8; }
.morph-item.chaos-postit-blue { background: #bfdbfe; }
.morph-item.chaos-postit-green { background: #bbf7d0; }
.morph-item.chaos-postit-orange { background: #fed7aa; }

.morph-item.node-state {
  background: #0d0d14;
  border-width: 1px;
  border-style: solid;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.4);
}
.morph-item.node-goal {
  border-color: rgba(139, 92, 246, 0.6);
  box-shadow: 0 0 24px rgba(139, 92, 246, 0.18), inset 0 0 24px rgba(139, 92, 246, 0.05);
}
.morph-item.node-task { border-color: rgba(96, 165, 250, 0.4); }
.morph-item.node-event { border-color: rgba(245, 158, 11, 0.4); }
.morph-item.node-contact { border-color: rgba(52, 211, 153, 0.4); }
.morph-item.node-note { border-color: rgba(168, 162, 158, 0.4); }

.morph-item.node-goal.progress-33 {
  box-shadow: 0 0 32px rgba(139, 92, 246, 0.28), inset 0 0 30px rgba(139, 92, 246, 0.08);
  transition: box-shadow 0.6s ease;
}
.morph-item.node-goal.progress-66 {
  box-shadow: 0 0 42px rgba(139, 92, 246, 0.4), inset 0 0 40px rgba(139, 92, 246, 0.12);
  transition: box-shadow 0.6s ease;
}
.morph-item.node-goal.progress-100 {
  border-color: rgba(139, 92, 246, 1);
  animation: goalCompletePulse 1.8s ease-in-out infinite;
}
@keyframes goalCompletePulse {
  0%, 100% { box-shadow: 0 0 50px rgba(139, 92, 246, 0.55), inset 0 0 45px rgba(139, 92, 246, 0.18); }
  50% { box-shadow: 0 0 80px rgba(139, 92, 246, 0.9), inset 0 0 60px rgba(139, 92, 246, 0.3); }
}

.morph-item.node-task.completing { animation: taskCompleteFlash 0.8s ease; }
@keyframes taskCompleteFlash {
  0% { box-shadow: 0 4px 12px rgba(0,0,0,0.4); }
  50% { box-shadow: 0 0 28px rgba(52, 211, 153, 0.7), 0 4px 12px rgba(0,0,0,0.4); }
  100% { box-shadow: 0 4px 12px rgba(0,0,0,0.4); }
}
.morph-item.node-task.completed {
  border-color: rgba(52, 211, 153, 0.5);
  opacity: 0.72;
  transition: all 0.5s ease;
}
.morph-item.node-task.completed .nf-title {
  text-decoration: line-through;
  text-decoration-color: rgba(52, 211, 153, 0.7);
  color: #a1a1aa;
}
.morph-item.node-task.completed .nf-badge {
  background: rgba(52, 211, 153, 0.18) !important;
  color: #6ee7b7 !important;
}
.morph-item.node-task.completed .nf-dot {
  background: #34d399 !important;
  box-shadow: 0 0 6px rgba(52, 211, 153, 0.7);
}

.nf-check {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 16px;
  height: 16px;
  background: #34d399;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0) rotate(-45deg);
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 0 12px rgba(52, 211, 153, 0.5);
}
.morph-item.completed .nf-check {
  opacity: 1;
  transform: scale(1) rotate(0);
}
.nf-check svg {
  width: 10px;
  height: 10px;
  stroke: #0a0a0f;
  stroke-width: 3;
}

.chaos-face {
  position: absolute;
  inset: 0;
  padding: 14px 16px;
  font-family: 'Kalam', cursive;
  font-size: 15px;
  color: #1a1a1a;
  font-weight: 400;
  line-height: 1.25;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  opacity: 1;
  transition: opacity 0.7s ease;
}
.morph-item.transformed .chaos-face { opacity: 0; }

.node-face {
  position: absolute;
  inset: 0;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  opacity: 0;
  transition: opacity 0.7s ease 0.5s;
}
.morph-item.transformed .node-face { opacity: 1; }

.nf-header { display: flex; align-items: center; gap: 6px; }
.nf-dot { width: 6px; height: 6px; border-radius: 50%; transition: all 0.4s ease; }
.nf-type {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #71717a;
  font-weight: 600;
}
.nf-title {
  font-size: 12px;
  font-weight: 500;
  color: #e4e4e7;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.5s ease;
}
.nf-badge {
  font-size: 9px;
  padding: 1px 7px;
  border-radius: 3px;
  display: inline-block;
  align-self: flex-start;
  font-weight: 500;
  transition: all 0.5s ease;
}
.nf-goal .nf-dot { background: rgb(139, 92, 246); box-shadow: 0 0 6px rgb(139, 92, 246); }
.nf-goal .nf-badge { background: rgba(139, 92, 246, 0.15); color: #c4b5fd; }
.nf-task .nf-dot { background: #60a5fa; }
.nf-task .nf-badge { background: rgba(96, 165, 250, 0.15); color: #93c5fd; }
.nf-event .nf-dot { background: #f59e0b; }
.nf-event .nf-badge { background: rgba(245, 158, 11, 0.15); color: #fcd34d; }
.nf-contact .nf-dot { background: #34d399; }
.nf-contact .nf-badge { background: rgba(52, 211, 153, 0.15); color: #6ee7b7; }
.nf-note .nf-dot { background: #a8a29e; }
.nf-note .nf-badge { background: rgba(168, 162, 158, 0.15); color: #d6d3d1; }

.chaos-deco {
  position: absolute;
  transition: opacity 1s ease, transform 1s ease;
  z-index: 3;
}
.chaos-deco.fading { opacity: 0 !important; transform: scale(0.8) !important; }

.paper-note {
  background: #fafaf9;
  padding: 10px 14px;
  font-family: 'Kalam', cursive;
  font-size: 12px;
  color: #3f3f46;
  border-radius: 1px;
  box-shadow: 0 4px 14px rgba(0,0,0,0.35);
  max-width: 180px;
  line-height: 1.5;
}
.paper-note .strike { text-decoration: line-through; opacity: 0.55; }
.text-bit {
  color: #71717a;
  font-size: 11px;
  font-family: 'Sora', sans-serif;
  opacity: 0.55;
  max-width: 180px;
  font-style: italic;
}

@keyframes socleFloatSubtle {
  0% { transform: translate(0, 0) rotate(var(--rot)); }
  100% { transform: translate(var(--dx), var(--dy)) rotate(calc(var(--rot) + 1deg)); }
}
.morph-item.chaos-state, .chaos-deco { animation: socleFloatSubtle 4s ease-in-out infinite alternate; }
.morph-item.transformed { animation: none; }
.morph-item.node-goal.progress-100 { animation: goalCompletePulse 1.8s ease-in-out infinite; }

#socle-canvas-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}
#socle-canvas-svg.visible { opacity: 1; }
.canvas-edge {
  fill: none;
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  transition: stroke-dashoffset 0.8s ease-out, stroke 0.5s ease;
}
.canvas-edge.drawn { stroke-dashoffset: 0; }
.canvas-edge.completed { stroke: rgba(52, 211, 153, 0.55) !important; }

.socle-view {
  position: absolute;
  inset: 0;
  z-index: 15;
  transform: translateX(100%);
  opacity: 0;
  transition: transform 1s cubic-bezier(0.7, 0, 0.3, 1), opacity 0.5s ease;
  pointer-events: none;
}
#socle-stage.phase-dashboard .view-dashboard {
  transform: translateX(0);
  opacity: 1;
  pointer-events: auto;
}
#socle-stage.phase-kanban .view-dashboard {
  transform: translateX(-100%);
  opacity: 1;
}
#socle-stage.phase-kanban .view-kanban {
  transform: translateX(0);
  opacity: 1;
  pointer-events: auto;
}

#socle-stage.phase-dashboard .morph-item,
#socle-stage.phase-dashboard #socle-canvas-svg,
#socle-stage.phase-kanban .morph-item,
#socle-stage.phase-kanban #socle-canvas-svg {
  opacity: 0;
  transition: opacity 0.6s ease;
}
#socle-stage.phase-completion .morph-item.transformed,
#socle-stage.phase-completion #socle-canvas-svg.visible {
  opacity: 1;
  transition: opacity 0.8s ease;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  padding: 24px;
  height: 100%;
}
.dash-widget {
  background: #0d0d14;
  border: 1px solid rgba(39, 39, 42, 0.9);
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  opacity: 0;
  transform: translateY(15px);
  transition: all 0.5s ease-out;
}
.dash-widget.revealed { opacity: 1; transform: translateY(0); }

.dw-header { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.dw-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}
.dw-title {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #a1a1aa;
  font-weight: 600;
}
.dw-count { margin-left: auto; font-size: 10px; color: #52525b; font-weight: 600; }
.dw-items { display: flex; flex-direction: column; gap: 7px; flex: 1; }
.dw-item { display: flex; align-items: center; gap: 8px; font-size: 11px; color: #d4d4d8; }
.dw-item-dot { width: 4px; height: 4px; border-radius: 50%; flex-shrink: 0; }
.dw-item-meta { margin-left: auto; font-size: 9px; color: #71717a; }

.dw-progress {
  margin-top: 5px;
  height: 3px;
  background: rgba(63, 63, 70, 0.5);
  border-radius: 2px;
  overflow: hidden;
}
.dw-progress-bar {
  height: 100%;
  background: rgb(139, 92, 246);
  width: 0%;
  transition: width 0.9s ease-out 0.3s, box-shadow 0.4s ease;
}
.dw-progress-bar.filled { width: var(--w); }
.dw-progress-bar.boosting {
  transition: width 0.7s ease-out, box-shadow 0.3s ease;
  box-shadow: 0 0 12px rgba(139, 92, 246, 0.9);
}

.progress-boost-badge {
  position: absolute;
  font-size: 9px;
  font-weight: 700;
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.2);
  border: 1px solid rgba(139, 92, 246, 0.5);
  padding: 2px 6px;
  border-radius: 4px;
  opacity: 0;
  transform: translateY(4px);
  transition: all 0.4s ease;
  pointer-events: none;
}
.progress-boost-badge.visible { opacity: 1; transform: translateY(0); }

.view-kanban { padding: 24px; }
.kanban-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.kanban-title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #a1a1aa;
  font-weight: 600;
}
.kanban-title-accent { color: #c4b5fd; }

.kanban-columns {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  height: calc(100% - 34px);
}
.kanban-col {
  background: #0d0d14;
  border: 1px solid rgba(39, 39, 42, 0.9);
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.col-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(63, 63, 70, 0.5);
}
.col-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.col-todo .col-dot { background: #71717a; }
.col-progress .col-dot { background: #60a5fa; box-shadow: 0 0 6px rgba(96, 165, 250, 0.5); }
.col-done .col-dot { background: #34d399; }
.col-title { font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 600; }
.col-todo .col-title { color: #a1a1aa; }
.col-progress .col-title { color: #93c5fd; }
.col-done .col-title { color: #6ee7b7; }
.col-count { margin-left: auto; font-size: 9px; color: #52525b; font-weight: 600; }
.col-cards { display: flex; flex-direction: column; gap: 8px; }

.kanban-card {
  background: #0a0a10;
  border: 1px solid rgba(63, 63, 70, 0.5);
  border-left: 3px solid;
  border-radius: 6px;
  padding: 9px 11px;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.45s ease-out, transform 0.45s ease-out;
}
.view-kanban.cards-visible .kanban-card { opacity: 1; transform: translateY(0); }
.col-todo .kanban-card { border-left-color: #71717a; }
.col-progress .kanban-card { border-left-color: #60a5fa; }
.col-done .kanban-card { border-left-color: #34d399; opacity: 0.5; }
.view-kanban.cards-visible .col-done .kanban-card { opacity: 0.65; }

.kc-title {
  font-size: 11px;
  color: #e4e4e7;
  font-weight: 500;
  margin-bottom: 6px;
  line-height: 1.3;
}
.col-done .kc-title {
  text-decoration: line-through;
  text-decoration-color: rgba(52, 211, 153, 0.4);
  color: #a1a1aa;
}
.kc-meta { display: flex; align-items: center; gap: 6px; }
.kc-badge {
  font-size: 8px;
  padding: 1px 6px;
  border-radius: 3px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.badge-high { background: rgba(239, 68, 68, 0.15); color: #fca5a5; }
.badge-medium { background: rgba(245, 158, 11, 0.15); color: #fcd34d; }
.badge-low { background: rgba(113, 113, 122, 0.25); color: #a1a1aa; }
.kc-meta-text { font-size: 9px; color: #71717a; margin-left: auto; }

.goal-modal {
  position: absolute;
  inset: 0;
  background: rgba(10, 10, 15, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.6s ease;
}
.goal-modal.visible {
  opacity: 1;
  pointer-events: auto;
}

.confetti-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 51;
}

.modal-content {
  position: relative;
  background: #0d0d14;
  border: 1px solid rgba(139, 92, 246, 0.4);
  border-radius: 16px;
  padding: 32px 40px 28px 40px;
  max-width: 440px;
  text-align: center;
  transform: scale(0.85) translateY(20px);
  opacity: 0;
  transition: all 0.55s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 52;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6), 0 0 60px rgba(139, 92, 246, 0.25);
}
.goal-modal.visible .modal-content {
  transform: scale(1) translateY(0);
  opacity: 1;
}

.trophy {
  display: inline-block;
  font-size: 58px;
  line-height: 1;
  margin-bottom: 14px;
  animation: trophyFloat 2.5s ease-in-out infinite;
  filter: drop-shadow(0 0 24px rgba(250, 204, 21, 0.5));
}
@keyframes trophyFloat {
  0%, 100% { transform: translateY(0) rotate(-4deg); }
  50% { transform: translateY(-9px) rotate(4deg); }
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #c4b5fd 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.modal-subtitle {
  font-size: 13px;
  color: #a1a1aa;
  font-weight: 300;
  margin-bottom: 22px;
}
.modal-badges {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.modal-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid;
}
.modal-badge .mb-icon { font-size: 13px; line-height: 1; }
.mb-goal { background: rgba(139, 92, 246, 0.12); border-color: rgba(139, 92, 246, 0.4); color: #c4b5fd; }
.mb-task { background: rgba(96, 165, 250, 0.12); border-color: rgba(96, 165, 250, 0.4); color: #93c5fd; }
.mb-contact { background: rgba(52, 211, 153, 0.12); border-color: rgba(52, 211, 153, 0.4); color: #6ee7b7; }
.mb-event { background: rgba(245, 158, 11, 0.12); border-color: rgba(245, 158, 11, 0.4); color: #fcd34d; }

.modal-buttons { display: flex; gap: 10px; justify-content: center; }
.modal-buttons button {
  padding: 9px 20px;
  border-radius: 9px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Sora', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid;
}
.btn-secondary {
  background: transparent;
  border-color: rgba(63, 63, 70, 0.9);
  color: #a1a1aa;
}
.btn-secondary:hover { border-color: rgba(113, 113, 122, 1); color: #d4d4d8; }
.btn-primary {
  background: rgb(139, 92, 246);
  border-color: rgb(139, 92, 246);
  color: white;
}
.btn-primary:hover { background: rgb(124, 58, 237); box-shadow: 0 0 18px rgba(139, 92, 246, 0.5); }

.phase-label {
  position: absolute;
  bottom: 16px;
  left: 16px;
  background: rgba(10, 10, 15, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(139, 92, 246, 0.25);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 500;
  color: #c4b5fd;
  z-index: 30;
  opacity: 0;
  transition: opacity 0.5s ease;
  pointer-events: none;
}
.phase-label.visible { opacity: 1; }

.socle-replay-btn {
  position: absolute;
  bottom: 16px;
  right: 16px;
  background: rgba(10, 10, 15, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(139, 92, 246, 0.35);
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 11px;
  font-weight: 500;
  color: #c4b5fd;
  cursor: pointer;
  z-index: 60;
  opacity: 0;
  transition: all 0.3s ease;
  font-family: 'Sora', sans-serif;
}
.socle-replay-btn.visible { opacity: 1; }
.socle-replay-btn:hover { background: rgba(139, 92, 246, 0.15); border-color: rgba(139, 92, 246, 0.6); }
`;

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

const gradientTextStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #ffffff 0%, #a3a3a3 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

export default function SocleDemoSection() {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const stageRef = useRef<HTMLDivElement>(null);
  const morphContainerRef = useRef<HTMLDivElement>(null);
  const canvasSvgRef = useRef<SVGSVGElement>(null);
  const phaseLabelRef = useRef<HTMLDivElement>(null);
  const replayBtnRef = useRef<HTMLButtonElement>(null);
  const confettiCanvasRef = useRef<HTMLCanvasElement>(null);
  const goalModalRef = useRef<HTMLDivElement>(null);
  const mainProgressRef = useRef<HTMLDivElement>(null);
  const progressLabelRef = useRef<HTMLSpanElement>(null);
  const boostBadgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stage = stageRef.current;
    const morphContainer = morphContainerRef.current;
    const canvasSvg = canvasSvgRef.current;
    const phaseLabel = phaseLabelRef.current;
    const replayBtn = replayBtnRef.current;
    const confettiCanvas = confettiCanvasRef.current;
    const goalModal = goalModalRef.current;

    if (
      !stage ||
      !morphContainer ||
      !canvasSvg ||
      !phaseLabel ||
      !replayBtn ||
      !confettiCanvas ||
      !goalModal
    )
      return;

    const timeouts: number[] = [];
    let confettiRAF: number | null = null;
    let confettiActive = false;
    let hasPlayed = false;

    const clearTimeouts = () => {
      timeouts.forEach((t) => window.clearTimeout(t));
      timeouts.length = 0;
    };

    const setLabel = (text: string) => {
      phaseLabel.textContent = text;
      phaseLabel.classList.add('visible');
    };
    const hideLabel = () => phaseLabel.classList.remove('visible');

    const buildChaos = () => {
      morphContainer.innerHTML = '';
      MORPH_ITEMS.forEach((item) => {
        const el = document.createElement('div');
        el.className = `morph-item chaos-state chaos-postit-${item.chaosColor}`;
        el.id = `socle-morph-${item.id}`;
        el.style.left = item.chaosX + '%';
        el.style.top = item.chaosY + '%';
        el.style.setProperty('--rot', item.chaosRot + 'deg');
        el.style.setProperty('--dx', Math.random() * 8 - 4 + 'px');
        el.style.setProperty('--dy', Math.random() * 8 - 4 + 'px');
        el.style.transform = `rotate(${item.chaosRot}deg)`;
        el.style.animationDelay = Math.random() * 2 + 's';
        el.innerHTML = `
          <div class="chaos-face">${item.chaosText}</div>
          <div class="node-face nf-${item.nodeType}">
            <div class="nf-header"><div class="nf-dot"></div><div class="nf-type">${item.nodeTypeName}</div></div>
            <div class="nf-title">${item.nodeTitle}</div>
            <div class="nf-badge">${item.nodeBadge}</div>
          </div>
          <div class="nf-check">
            <svg viewBox="0 0 16 16" fill="none"><path d="M3 8l4 4 6-7" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </div>
        `;
        morphContainer.appendChild(el);
      });
      DECO_ITEMS.forEach((item) => {
        const el = document.createElement('div');
        el.className = 'chaos-deco';
        el.style.left = item.x + '%';
        el.style.top = item.y + '%';
        el.style.setProperty('--rot', item.rot + 'deg');
        el.style.setProperty('--dx', Math.random() * 6 - 3 + 'px');
        el.style.setProperty('--dy', Math.random() * 6 - 3 + 'px');
        el.style.transform = `rotate(${item.rot}deg)`;
        el.style.animationDelay = Math.random() * 2 + 's';
        if (item.type === 'paper') el.innerHTML = `<div class="paper-note">${item.html}</div>`;
        else if (item.type === 'text') el.innerHTML = `<div class="text-bit">${item.text}</div>`;
        morphContainer.appendChild(el);
      });
    };

    const startMorph = () => {
      morphContainer.querySelectorAll('.chaos-deco').forEach((el) => el.classList.add('fading'));
      MORPH_ITEMS.forEach((item, i) => {
        const el = document.getElementById(`socle-morph-${item.id}`);
        if (!el) return;
        const t = window.setTimeout(() => {
          el.style.left = `calc(${item.targetX}% - 85px)`;
          el.style.top = `calc(${item.targetY}% - 41px)`;
          el.style.transform = 'rotate(0deg)';
          el.classList.remove('chaos-state', `chaos-postit-${item.chaosColor}`);
          el.classList.add('node-state', `node-${item.nodeType}`, 'transformed');
        }, i * 80);
        timeouts.push(t);
      });
    };

    const drawEdges = () => {
      canvasSvg.innerHTML = '';
      canvasSvg.classList.add('visible');
      const cW = stage.clientWidth;
      const cH = stage.clientHeight;
      canvasSvg.setAttribute('viewBox', `0 0 ${cW} ${cH}`);
      const nodeSize = { w: 170, h: 82 };
      const posMap: Record<string, { topX: number; topY: number; botX: number; botY: number }> = {};
      MORPH_ITEMS.forEach((item) => {
        const cx = (item.targetX / 100) * cW;
        const cy = (item.targetY / 100) * cH;
        posMap[item.id] = {
          topX: cx,
          topY: cy - nodeSize.h / 2,
          botX: cx,
          botY: cy + nodeSize.h / 2,
        };
      });
      EDGES.forEach((edge, i) => {
        const from = posMap[edge.from];
        const to = posMap[edge.to];
        if (!from || !to) return;
        const x1 = from.botX,
          y1 = from.botY,
          x2 = to.topX,
          y2 = to.topY;
        const dy = Math.abs(y2 - y1);
        const t = Math.min(Math.max(dy * 0.55, 35), 130);
        const d = `M ${x1} ${y1} C ${x1} ${y1 + t}, ${x2} ${y2 - t}, ${x2} ${y2}`;
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', d);
        path.setAttribute('class', 'canvas-edge');
        path.setAttribute('stroke', edge.color);
        path.setAttribute('pathLength', '100');
        path.dataset.edgeId = edge.id;
        canvasSvg.appendChild(path);
        const to2 = window.setTimeout(() => path.classList.add('drawn'), i * 120);
        timeouts.push(to2);
      });
    };

    const showDashboard = () => {
      stage.querySelectorAll('.dash-widget').forEach((w) => {
        const delay = parseInt((w as HTMLElement).dataset.delay || '0');
        const t = window.setTimeout(() => w.classList.add('revealed'), delay);
        timeouts.push(t);
      });
      const t2 = window.setTimeout(() => {
        stage.querySelectorAll('.dw-progress-bar').forEach((b) => b.classList.add('filled'));
      }, 650);
      timeouts.push(t2);
    };

    const boostProgress = () => {
      const bar = mainProgressRef.current;
      const label = progressLabelRef.current;
      const badge = boostBadgeRef.current;
      if (!bar || !label || !badge) return;
      bar.classList.add('boosting');
      bar.style.setProperty('--w', bar.dataset.boost + '%');
      let current = 45;
      const target = 52;
      const step = () => {
        if (current < target) {
          current++;
          label.textContent = current + '%';
          const t = window.setTimeout(step, 60);
          timeouts.push(t);
        }
      };
      timeouts.push(window.setTimeout(step, 100));
      badge.classList.add('visible');
      timeouts.push(window.setTimeout(() => badge.classList.remove('visible'), 1800));
      timeouts.push(window.setTimeout(() => bar.classList.remove('boosting'), 1000));
    };

    const hideDashboard = () => {
      stage.querySelectorAll('.dash-widget').forEach((w) => w.classList.remove('revealed'));
      stage
        .querySelectorAll('.dw-progress-bar')
        .forEach((b) => b.classList.remove('filled', 'boosting'));
      const bar = mainProgressRef.current;
      const label = progressLabelRef.current;
      const badge = boostBadgeRef.current;
      if (bar) bar.style.setProperty('--w', '45%');
      if (label) label.textContent = '45%';
      if (badge) badge.classList.remove('visible');
    };

    const showKanban = () => {
      const view = stage.querySelector('.view-kanban');
      if (!view) return;
      timeouts.push(window.setTimeout(() => view.classList.add('cards-visible'), 100));
    };

    const hideKanban = () => {
      const view = stage.querySelector('.view-kanban');
      if (view) view.classList.remove('cards-visible');
    };

    const completeTask = (taskId: string) => {
      const taskEl = document.getElementById(`socle-morph-${taskId}`);
      if (!taskEl) return;
      taskEl.classList.add('completing');
      timeouts.push(
        window.setTimeout(() => {
          taskEl.classList.add('completed');
          taskEl.classList.remove('completing');
        }, 400)
      );
      const edgePath = canvasSvg.querySelector(`path[data-edge-id="${taskId}-obj"]`);
      if (edgePath) {
        timeouts.push(window.setTimeout(() => edgePath.classList.add('completed'), 500));
      }
    };

    const updateGoalBadge = (text: string) => {
      const goalEl = document.getElementById('socle-morph-obj');
      if (!goalEl) return;
      const badge = goalEl.querySelector('.nf-badge');
      if (badge) badge.textContent = text;
    };

    const setGoalProgress = (level: number) => {
      const goalEl = document.getElementById('socle-morph-obj');
      if (!goalEl) return;
      goalEl.classList.remove('progress-33', 'progress-66', 'progress-100');
      if (level === 33) goalEl.classList.add('progress-33');
      else if (level === 66) goalEl.classList.add('progress-66');
      else if (level === 100) goalEl.classList.add('progress-100');
    };

    const launchConfetti = () => {
      const ctx = confettiCanvas.getContext('2d');
      if (!ctx) return;
      const rect = confettiCanvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      confettiCanvas.width = rect.width * dpr;
      confettiCanvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const colors = ['#a78bfa', '#fbcfe8', '#fcd34d', '#6ee7b7', '#93c5fd', '#f472b6', '#fde68a'];
      const particles: Array<{
        x: number;
        y: number;
        vx: number;
        vy: number;
        size: number;
        color: string;
        rotation: number;
        rotationSpeed: number;
        life: number;
        decay: number;
      }> = [];
      const cx = rect.width / 2;
      const cy = rect.height / 2 - 40;

      for (let i = 0; i < 100; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 8 + 3;
        particles.push({
          x: cx + (Math.random() - 0.5) * 40,
          y: cy + (Math.random() - 0.5) * 20,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 4,
          size: Math.random() * 5 + 3,
          color: colors[Math.floor(Math.random() * colors.length)],
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.28,
          life: 1,
          decay: Math.random() * 0.004 + 0.006,
        });
      }

      confettiActive = true;

      const draw = () => {
        if (!confettiActive) return;
        ctx.clearRect(0, 0, rect.width, rect.height);
        let alive = false;
        particles.forEach((p) => {
          if (p.life <= 0) return;
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.22;
          p.vx *= 0.99;
          p.rotation += p.rotationSpeed;
          p.life -= p.decay;
          if (p.life > 0) {
            alive = true;
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rotation);
            ctx.fillStyle = p.color;
            ctx.globalAlpha = Math.max(p.life, 0);
            ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 1.4);
            ctx.restore();
          }
        });
        if (alive) confettiRAF = requestAnimationFrame(draw);
        else confettiActive = false;
      };
      draw();
    };

    const stopConfetti = () => {
      confettiActive = false;
      if (confettiRAF) cancelAnimationFrame(confettiRAF);
      confettiRAF = null;
      const ctx = confettiCanvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, confettiCanvas.width, confettiCanvas.height);
    };

    const showGoalModal = () => {
      goalModal.classList.add('visible');
      timeouts.push(window.setTimeout(() => launchConfetti(), 200));
      timeouts.push(window.setTimeout(() => launchConfetti(), 1200));
    };

    const hideGoalModal = () => {
      goalModal.classList.remove('visible');
      stopConfetti();
    };

    const playSequence = () => {
      clearTimeouts();
      replayBtn.classList.remove('visible');
      hideLabel();
      hideDashboard();
      hideKanban();
      hideGoalModal();
      canvasSvg.classList.remove('visible');
      stage.className = '';
      buildChaos();

      // Phase 1: Chaos (0 - 2.5s)
      timeouts.push(
        window.setTimeout(() => setLabel(tRef.current('landing.socle_phase_chaos')), 500)
      );

      // Phase 2: Canvas morph (2.7 - 7.4s)
      timeouts.push(window.setTimeout(() => hideLabel(), 2400));
      timeouts.push(
        window.setTimeout(() => {
          stage.classList.add('phase-canvas');
          startMorph();
          setLabel(tRef.current('landing.socle_phase_canvas'));
        }, 2700)
      );
      timeouts.push(window.setTimeout(() => drawEdges(), 4400));

      // Phase 3A: Dashboard (7.8 - 12s)
      timeouts.push(window.setTimeout(() => hideLabel(), 7400));
      timeouts.push(
        window.setTimeout(() => {
          stage.classList.remove('phase-canvas');
          stage.classList.add('phase-dashboard');
          showDashboard();
          setLabel(tRef.current('landing.socle_phase_views'));
        }, 7800)
      );
      timeouts.push(window.setTimeout(() => boostProgress(), 10000));

      // Phase 3B: Kanban (12 - 15.5s)
      timeouts.push(
        window.setTimeout(() => {
          stage.classList.remove('phase-dashboard');
          stage.classList.add('phase-kanban');
        }, 12000)
      );
      timeouts.push(window.setTimeout(() => showKanban(), 12700));

      // Phase 3C: Return to canvas (15.5 - 17s)
      timeouts.push(
        window.setTimeout(() => {
          hideLabel();
          stage.classList.remove('phase-kanban');
          stage.classList.add('phase-completion');
        }, 15500)
      );

      // Phase 4: Task completion (17 - 20s)
      timeouts.push(
        window.setTimeout(() => setLabel(tRef.current('landing.socle_phase_progress')), 16700)
      );
      timeouts.push(
        window.setTimeout(() => {
          completeTask('t1');
          updateGoalBadge('1 / 3');
          setGoalProgress(33);
        }, 17000)
      );
      timeouts.push(
        window.setTimeout(() => {
          completeTask('t2');
          updateGoalBadge('2 / 3');
          setGoalProgress(66);
        }, 18000)
      );
      timeouts.push(
        window.setTimeout(() => {
          completeTask('t3');
          updateGoalBadge(tRef.current('landing.socle_goal_achieved'));
          setGoalProgress(100);
        }, 19000)
      );

      // Phase 5: Goal modal (20.5 - 24s)
      timeouts.push(window.setTimeout(() => hideLabel(), 20000));
      timeouts.push(window.setTimeout(() => showGoalModal(), 20500));

      // Replay
      timeouts.push(window.setTimeout(() => replayBtn.classList.add('visible'), 23500));
    };

    // Build initial chaos
    buildChaos();

    // IntersectionObserver trigger
    const stageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasPlayed) {
            hasPlayed = true;
            playSequence();
            stageObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.35 }
    );
    stageObserver.observe(stage);

    // Replay button handler
    const handleReplay = () => playSequence();
    replayBtn.addEventListener('click', handleReplay);

    return () => {
      stageObserver.disconnect();
      replayBtn.removeEventListener('click', handleReplay);
      clearTimeouts();
      stopConfetti();
    };
  }, []);

  return (
    <section className="py-20 md:py-32 px-6">
      <style>{SOCLE_CSS}</style>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16 reveal">
          <span className="inline-flex items-center gap-2 mb-6 px-3 py-1 text-xs font-medium tracking-wide text-emerald-400 border border-emerald-500/30 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            {t('landing.v1_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={gradientTextStyle}>
            {t('landing.v1_heading')}
          </h2>
          <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto">
            {t('landing.v1_description')}
          </p>
        </div>

        <div className="reveal">
          <div id="socle-stage" ref={stageRef}>
            <div className="phase-label" ref={phaseLabelRef} />
            <button className="socle-replay-btn" ref={replayBtnRef} type="button">
              {t('landing.socle_replay')}
            </button>

            <svg id="socle-canvas-svg" ref={canvasSvgRef} xmlns="http://www.w3.org/2000/svg" />
            <div className="absolute inset-0" ref={morphContainerRef} />

            {/* VIEW DASHBOARD */}
            <div className="socle-view view-dashboard">
              <div className="dashboard-grid">
                <div className="dash-widget" data-delay="0">
                  <div className="dw-header">
                    <div
                      className="dw-icon"
                      style={{ background: 'rgba(96,165,250,0.15)', color: '#93c5fd' }}
                    >
                      ☀
                    </div>
                    <div className="dw-title">{t('landing.socle_dash_today')}</div>
                    <div className="dw-count">3</div>
                  </div>
                  <div className="dw-items">
                    <div className="dw-item">
                      <span className="dw-item-dot" style={{ background: '#60a5fa' }} />
                      <span>Reserver les vols</span>
                      <span className="dw-item-meta">Haute</span>
                    </div>
                    <div className="dw-item">
                      <span className="dw-item-dot" style={{ background: '#60a5fa' }} />
                      <span>Appeler l'agence</span>
                      <span className="dw-item-meta">Moyenne</span>
                    </div>
                    <div className="dw-item">
                      <span className="dw-item-dot" style={{ background: '#f59e0b' }} />
                      <span>Rappel passeport</span>
                      <span className="dw-item-meta">14h00</span>
                    </div>
                  </div>
                </div>

                <div className="dash-widget" data-delay="150">
                  <div className="dw-header">
                    <div
                      className="dw-icon"
                      style={{ background: 'rgba(139,92,246,0.15)', color: '#c4b5fd' }}
                    >
                      ◎
                    </div>
                    <div className="dw-title">{t('landing.socle_dash_goals')}</div>
                    <div className="dw-count">2</div>
                  </div>
                  <div className="dw-items">
                    <div className="dw-item">
                      <div className="flex-1 relative">
                        <div className="flex items-center gap-2">
                          <span className="dw-item-dot" style={{ background: '#a78bfa' }} />
                          <span>Voyage au Japon</span>
                          <span className="dw-item-meta" ref={progressLabelRef}>
                            45%
                          </span>
                        </div>
                        <div className="dw-progress">
                          <div
                            className="dw-progress-bar"
                            ref={mainProgressRef}
                            style={{ ['--w' as string]: '45%' }}
                            data-boost="52"
                          />
                        </div>
                        <div
                          className="progress-boost-badge"
                          ref={boostBadgeRef}
                          style={{ right: 0, top: -2 }}
                        >
                          +7%
                        </div>
                      </div>
                    </div>
                    <div className="dw-item">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="dw-item-dot" style={{ background: '#a78bfa' }} />
                          <span>Apprendre les bases en japonais</span>
                          <span className="dw-item-meta">30%</span>
                        </div>
                        <div className="dw-progress">
                          <div className="dw-progress-bar" style={{ ['--w' as string]: '30%' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="dash-widget" data-delay="300">
                  <div className="dw-header">
                    <div
                      className="dw-icon"
                      style={{ background: 'rgba(245,158,11,0.15)', color: '#fcd34d' }}
                    >
                      ▲
                    </div>
                    <div className="dw-title">{t('landing.socle_dash_upcoming')}</div>
                    <div className="dw-count">2</div>
                  </div>
                  <div className="dw-items">
                    <div className="dw-item">
                      <span className="dw-item-dot" style={{ background: '#f59e0b' }} />
                      <span>Vol depart Tokyo</span>
                      <span className="dw-item-meta">15 mai</span>
                    </div>
                    <div className="dw-item">
                      <span className="dw-item-dot" style={{ background: '#f59e0b' }} />
                      <span>Cours de japonais</span>
                      <span className="dw-item-meta">2 mai</span>
                    </div>
                  </div>
                </div>

                <div className="dash-widget" data-delay="450">
                  <div className="dw-header">
                    <div
                      className="dw-icon"
                      style={{ background: 'rgba(52,211,153,0.15)', color: '#6ee7b7' }}
                    >
                      ◉
                    </div>
                    <div className="dw-title">{t('landing.socle_dash_contacts')}</div>
                    <div className="dw-count">3</div>
                  </div>
                  <div className="dw-items">
                    <div className="dw-item">
                      <span className="dw-item-dot" style={{ background: '#34d399' }} />
                      <span>Keiko Yamamoto</span>
                      <span className="dw-item-meta">Tokyo</span>
                    </div>
                    <div className="dw-item">
                      <span className="dw-item-dot" style={{ background: '#34d399' }} />
                      <span>Ryo Sato</span>
                      <span className="dw-item-meta">Prof jap.</span>
                    </div>
                    <div className="dw-item">
                      <span className="dw-item-dot" style={{ background: '#34d399' }} />
                      <span>Emma Martin</span>
                      <span className="dw-item-meta">Agence</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* VIEW KANBAN */}
            <div className="socle-view view-kanban">
              <div className="kanban-header">
                <div className="kanban-title">
                  Kanban — <span className="kanban-title-accent">Voyage au Japon</span>
                </div>
              </div>
              <div className="kanban-columns">
                <div className="kanban-col col-todo">
                  <div className="col-header">
                    <div className="col-dot" />
                    <div className="col-title">{t('landing.socle_kanban_todo')}</div>
                    <div className="col-count">3</div>
                  </div>
                  <div className="col-cards">
                    <div className="kanban-card">
                      <div className="kc-title">Preparer les valises</div>
                      <div className="kc-meta">
                        <span className="kc-badge badge-low">Basse</span>
                        <span className="kc-meta-text">10 mai</span>
                      </div>
                    </div>
                    <div className="kanban-card">
                      <div className="kc-title">Acheter les guides</div>
                      <div className="kc-meta">
                        <span className="kc-badge badge-medium">Moyenne</span>
                        <span className="kc-meta-text">1 mai</span>
                      </div>
                    </div>
                    <div className="kanban-card">
                      <div className="kc-title">Reserver hotels Kyoto</div>
                      <div className="kc-meta">
                        <span className="kc-badge badge-high">Haute</span>
                        <span className="kc-meta-text">25 avril</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="kanban-col col-progress">
                  <div className="col-header">
                    <div className="col-dot" />
                    <div className="col-title">{t('landing.socle_kanban_progress')}</div>
                    <div className="col-count">2</div>
                  </div>
                  <div className="col-cards">
                    <div className="kanban-card">
                      <div className="kc-title">Reserver les vols</div>
                      <div className="kc-meta">
                        <span className="kc-badge badge-high">Haute</span>
                        <span className="kc-meta-text">Cette sem.</span>
                      </div>
                    </div>
                    <div className="kanban-card">
                      <div className="kc-title">Planifier l'itineraire</div>
                      <div className="kc-meta">
                        <span className="kc-badge badge-medium">Moyenne</span>
                        <span className="kc-meta-text">20 avril</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="kanban-col col-done">
                  <div className="col-header">
                    <div className="col-dot" />
                    <div className="col-title">{t('landing.socle_kanban_done')}</div>
                    <div className="col-count">2</div>
                  </div>
                  <div className="col-cards">
                    <div className="kanban-card">
                      <div className="kc-title">Demander le visa</div>
                      <div className="kc-meta">
                        <span className="kc-badge badge-high">Haute</span>
                        <span className="kc-meta-text">5 avril</span>
                      </div>
                    </div>
                    <div className="kanban-card">
                      <div className="kc-title">Appeler l'agence</div>
                      <div className="kc-meta">
                        <span className="kc-badge badge-medium">Moyenne</span>
                        <span className="kc-meta-text">3 avril</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* GOAL COMPLETION MODAL */}
            <div className="goal-modal" ref={goalModalRef}>
              <canvas className="confetti-canvas" ref={confettiCanvasRef} />
              <div className="modal-content">
                <div className="trophy">🏆</div>
                <h3 className="modal-title">{t('landing.socle_goal_title')}</h3>
                <p className="modal-subtitle">{t('landing.socle_goal_subtitle')}</p>
                <div className="modal-badges">
                  <div className="modal-badge mb-goal">
                    <span className="mb-icon">◎</span>
                    <span>Voyage au Japon</span>
                  </div>
                  <div className="modal-badge mb-task">
                    <span className="mb-icon">◆</span>
                    <span>{t('landing.socle_goal_tasks')}</span>
                  </div>
                  <div className="modal-badge mb-event">
                    <span className="mb-icon">▲</span>
                    <span>{t('landing.socle_goal_event')}</span>
                  </div>
                  <div className="modal-badge mb-contact">
                    <span className="mb-icon">◉</span>
                    <span>{t('landing.socle_goal_contact')}</span>
                  </div>
                </div>
                <div className="modal-buttons">
                  <button type="button" className="btn-secondary">
                    {t('landing.socle_goal_close')}
                  </button>
                  <button type="button" className="btn-primary">
                    {t('landing.socle_goal_timeline')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-2">
                {t('landing.socle_feature_canvas_title')}
              </h3>
              <p className="text-xs text-zinc-400 leading-relaxed">
                {t('landing.socle_feature_canvas_desc')}
              </p>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-2">
                {t('landing.socle_feature_entities_title')}
              </h3>
              <p className="text-xs text-zinc-400 leading-relaxed">
                {t('landing.socle_feature_entities_desc')}
              </p>
            </div>
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-2">
                {t('landing.socle_feature_views_title')}
              </h3>
              <p className="text-xs text-zinc-400 leading-relaxed">
                {t('landing.socle_feature_views_desc')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
